import React, { useState, useEffect, useRef } from 'react';
import { Users, FileText, Key, Plus, CreditCard as Edit, Trash2, Save, X, Search, Download, CheckCircle, Clock, DollarSign, Calendar, Building, Mail, Phone, Briefcase, MapPin, Upload, ChevronDown, ChevronUp, MoreVertical, Pause, Play, Trash, RotateCcw, Shield } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabaseClient';
import LoginSecurityTab from '../../components/admin/LoginSecurityTab';

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company_id: string;
  phone?: string;
  title?: string;
  country_region?: string;
  company_size?: string;
  hiring_needs?: string;
  budget_range?: string;
  timeline?: string;
  project_requirements?: string;
  role?: string;
  status?: string;
  soft_deleted_at?: string | null;
  soft_deleted_by?: string | null;
  companies?: {
    name: string;
    industry?: string;
    size?: string;
    website?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  deletion_request?: {
    id: string;
    status: string;
    reason?: string;
    requested_at: string;
  } | null;
}

interface InterviewRequest {
  id: string;
  consultant_id: string;
  client_id: string;
  consultant_name: string;
  consultant_modules: string;
  consultant_experience: string;
  client_company: string;
  client_contact_name: string;
  client_email: string;
  client_phone: string;
  requested_interview_date: string | null;
  notes: string;
  admin_notes: string | null;
  admin_notes_created_at: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  workday_client_profiles?: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    title?: string;
    country_region?: string;
    companies?: {
      name: string;
      website?: string;
      industry?: string;
      size?: string;
      city?: string;
      state?: string;
      country?: string;
    };
  };
}

interface Invoice {
  id: string;
  customer_profile_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  amount: number;
  description: string;
  invoice_url: string | null;
  is_paid: boolean;
  paid_date: string | null;
}

interface Credential {
  id: string;
  customer_profile_id: string;
  username: string;
  password: string;
  login_url: string;
  access_granted_date: string;
  notes: string;
}

interface Resource {
  id: string;
  customer_profile_id: string | null;
  title: string;
  description: string;
  resource_url: string;
  resource_type: string;
  is_global: boolean;
}

export default function WorkdayClientManagement() {
  const [activeTab, setActiveTab] = useState<'clients' | 'interviews' | 'invoices' | 'credentials' | 'resources' | 'login-security'>('clients');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [interviewRequests, setInterviewRequests] = useState<InterviewRequest[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<InterviewRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [clientProcessing, setClientProcessing] = useState(false);
  const [clientMessage, setClientMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [clientConfirmModal, setClientConfirmModal] = useState<{
    type: 'soft_delete' | 'hard_delete' | 'restore';
    customer: Customer;
  } | null>(null);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Customer | null>(null);

  useEffect(() => {
    fetchData();

    // Use a unique channel ID per mount so re-navigation never reuses a stale channel
    const uid = Date.now();

    const profilesSubscription = supabase
      .channel(`wcm_profiles_${uid}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workday_client_profiles' }, () => {
        fetchCustomers();
        fetchInterviewRequests();
      })
      .subscribe();

    const companiesSubscription = supabase
      .channel(`wcm_companies_${uid}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'companies' }, () => {
        fetchCustomers();
        fetchInterviewRequests();
      })
      .subscribe();

    const interviewsSubscription = supabase
      .channel(`wcm_interviews_${uid}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'interview_requests' }, () => {
        fetchCustomers();
        fetchInterviewRequests();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(profilesSubscription);
      supabase.removeChannel(companiesSubscription);
      supabase.removeChannel(interviewsSubscription);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await fetchCustomers();

    if (activeTab === 'clients') {
      // Already fetched customers
    } else if (activeTab === 'interviews') {
      await fetchInterviewRequests();
    } else if (activeTab === 'invoices') {
      await fetchInvoices();
    } else if (activeTab === 'credentials') {
      await fetchCredentials();
    } else if (activeTab === 'resources') {
      await fetchResources();
    }

    setLoading(false);
  };

  const fetchCustomers = async () => {
    const { data: profilesData, error: profilesError } = await supabase
      .from('workday_client_profiles')
      .select(`
        *,
        companies:company_id (
          id,
          name,
          industry,
          size,
          website,
          address,
          city,
          state,
          country,
          postal_code,
          updated_at
        )
      `)
      .order('updated_at', { ascending: false });

    if (profilesError || !profilesData) {
      console.error('Error fetching profiles:', profilesError);
      return;
    }

    const clientIds = profilesData.map((p: any) => p.id);
    let deletionRequests: any[] = [];
    if (clientIds.length > 0) {
      const { data: drData } = await supabase
        .from('account_deletion_requests')
        .select('id, user_id, status, reason, requested_at')
        .in('user_id', clientIds)
        .eq('user_type', 'workday_client')
        .eq('status', 'pending');
      deletionRequests = drData || [];
    }

    const enriched = profilesData.map((p: any) => ({
      ...p,
      deletion_request: deletionRequests.find((d: any) => d.user_id === p.id) || null
    }));

    setCustomers(enriched);
  };

  const fetchInterviewRequests = async () => {
    const { data, error } = await supabase
      .from('interview_requests')
      .select(`
        *,
        workday_client_profiles!interview_requests_client_id_fkey (
          first_name,
          last_name,
          email,
          phone,
          title,
          country_region,
          company_id,
          companies:company_id (
            name,
            website,
            industry,
            size,
            city,
            state,
            country
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setInterviewRequests(data);
    }
  };

  const fetchInvoices = async () => {
    const { data, error } = await supabase
      .from('workday_client_invoices')
      .select('*')
      .order('invoice_date', { ascending: false });

    if (!error && data) {
      setInvoices(data);
    }
  };

  const fetchCredentials = async () => {
    const { data, error } = await supabase
      .from('workday_client_credentials')
      .select('*')
      .order('access_granted_date', { ascending: false });

    if (!error && data) {
      setCredentials(data);
    }
  };

  const fetchResources = async () => {
    const { data, error } = await supabase
      .from('workday_client_resources')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setResources(data);
    }
  };

  const handleSaveInvoice = async (invoice: Partial<Invoice>) => {
    if (invoice.id) {
      const { error } = await supabase
        .from('workday_client_invoices')
        .update(invoice)
        .eq('id', invoice.id);

      if (!error) {
        await fetchInvoices();
        setShowModal(false);
        setEditingItem(null);
      }
    } else {
      const { error } = await supabase
        .from('workday_client_invoices')
        .insert([invoice]);

      if (!error) {
        await fetchInvoices();
        setShowModal(false);
        setEditingItem(null);
      }
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    if (confirm('Are you sure you want to delete this invoice?')) {
      const { error } = await supabase
        .from('workday_client_invoices')
        .delete()
        .eq('id', id);

      if (!error) {
        await fetchInvoices();
      }
    }
  };

  const handleSaveCredential = async (credential: Partial<Credential>) => {
    if (credential.id) {
      const { error } = await supabase
        .from('workday_client_credentials')
        .update(credential)
        .eq('id', credential.id);

      if (!error) {
        await fetchCredentials();
        setShowModal(false);
        setEditingItem(null);
      }
    } else {
      const { error } = await supabase
        .from('workday_client_credentials')
        .insert([credential]);

      if (!error) {
        await fetchCredentials();
        setShowModal(false);
        setEditingItem(null);
      }
    }
  };

  const handleDeleteCredential = async (id: string) => {
    if (confirm('Are you sure you want to delete these credentials?')) {
      const { error } = await supabase
        .from('workday_client_credentials')
        .delete()
        .eq('id', id);

      if (!error) {
        await fetchCredentials();
      }
    }
  };

  const handleSaveResource = async (resource: Partial<Resource>) => {
    if (resource.id) {
      const { error } = await supabase
        .from('workday_client_resources')
        .update(resource)
        .eq('id', resource.id);

      if (!error) {
        await fetchResources();
        setShowModal(false);
        setEditingItem(null);
      }
    } else {
      const { error } = await supabase
        .from('workday_client_resources')
        .insert([resource]);

      if (!error) {
        await fetchResources();
        setShowModal(false);
        setEditingItem(null);
      }
    }
  };

  const handleDeleteResource = async (id: string) => {
    if (confirm('Are you sure you want to delete this resource?')) {
      const { error } = await supabase
        .from('workday_client_resources')
        .delete()
        .eq('id', id);

      if (!error) {
        await fetchResources();
      }
    }
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.first_name} ${customer.last_name}` : 'Unknown';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getCustomerName(invoice.customer_profile_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCredentials = credentials.filter(cred =>
    cred.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getCustomerName(cred.customer_profile_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredResources = resources.filter(resource =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCustomers = customers.filter(customer =>
    customer.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.companies?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInterviewRequests = interviewRequests.filter(request => {
    const companyName = request.workday_client_profiles?.companies?.name || request.client_company;
    const contactName = request.workday_client_profiles
      ? `${request.workday_client_profiles.first_name} ${request.workday_client_profiles.last_name}`
      : request.client_contact_name;

    return request.consultant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.status.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleResumeUpload = async (consultantId: string): Promise<string | null> => {
    if (!resumeFile) return null;

    try {
      const fileExt = resumeFile.name.split('.').pop();
      const fileName = `${consultantId}_${Date.now()}.${fileExt}`;
      const filePath = `${consultantId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(filePath, resumeFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert('Failed to upload resume. Please try again.');
      return null;
    }
  };

  const handleUpdateInterviewStatus = async (id: string, status: string, notes?: string) => {
    try {
      console.log('Updating interview request:', { id, status, notes, hasResume: !!resumeFile });
      setUploadingResume(true);

      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updateData.admin_notes = notes;
        updateData.admin_notes_created_at = new Date().toISOString();
      }

      let resumeUrl = null;
      if (resumeFile && selectedRequest) {
        resumeUrl = await handleResumeUpload(selectedRequest.consultant_id);
        if (!resumeUrl) {
          setUploadingResume(false);
          return;
        }

        updateData.resume_url = resumeUrl;
        updateData.resume_file_name = resumeFile.name;

        const { error: docError } = await supabase
          .from('consultant_documents')
          .insert({
            consultant_id: selectedRequest.consultant_id,
            document_type: 'resume',
            document_url: resumeUrl,
            file_name: resumeFile.name,
            uploaded_by: 'admin'
          });

        if (docError) {
          console.error('Error saving document record:', docError);
        }
      }

      console.log('Updating database with:', updateData);
      const { error } = await supabase
        .from('interview_requests')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('Error updating interview request:', error);
        alert(`Failed to update interview request: ${error.message}`);
        setUploadingResume(false);
        return;
      }

      console.log('Interview request updated successfully');
      await fetchInterviewRequests();
      setShowNotesModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      setResumeFile(null);
      setUploadingResume(false);
      alert(`Interview request ${status === 'approved' ? 'approved' : 'rejected'} successfully! The client has been notified.`);
    } catch (error) {
      console.error('Error in handleUpdateInterviewStatus:', error);
      alert('An unexpected error occurred. Please try again.');
      setUploadingResume(false);
    }
  };

  const openNotesModal = (request: InterviewRequest, newStatus: string) => {
    setSelectedRequest({ ...request, status: newStatus });
    setAdminNotes(request.admin_notes || '');
    setResumeFile(null);
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setSelectedRequest(null);
    setAdminNotes('');
    setResumeFile(null);
  };

  const showClientMessage = (type: 'success' | 'error', text: string) => {
    setClientMessage({ type, text });
    setTimeout(() => setClientMessage(null), 4000);
  };

  const handlePauseClient = async (customer: Customer) => {
    const newStatus = customer.status === 'paused' ? 'active' : 'paused';
    try {
      const { error } = await supabase
        .from('workday_client_profiles')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', customer.id);
      if (error) throw error;
      showClientMessage('success', `Account ${newStatus === 'paused' ? 'paused' : 'activated'} successfully`);
      await fetchCustomers();
    } catch {
      showClientMessage('error', 'Failed to update account status');
    }
  };

  const handleSoftDeleteClient = async (customer: Customer) => {
    setClientProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('workday_client_profiles')
        .update({
          soft_deleted_at: new Date().toISOString(),
          soft_deleted_by: user?.id,
          status: 'paused',
          updated_at: new Date().toISOString(),
        })
        .eq('id', customer.id);
      if (error) throw error;
      showClientMessage('success', 'Account soft deleted. User cannot log in. Data held for 30 days.');
      setClientConfirmModal(null);
      await fetchCustomers();
    } catch {
      showClientMessage('error', 'Failed to soft delete account');
    } finally {
      setClientProcessing(false);
    }
  };

  const handleHardDeleteClient = async (customer: Customer) => {
    setClientProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-delete-client`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ client_profile_id: customer.id }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      showClientMessage('success', 'Account permanently deleted.');
      setClientConfirmModal(null);
      await fetchCustomers();
    } catch (err: any) {
      showClientMessage('error', err.message || 'Failed to permanently delete account');
    } finally {
      setClientProcessing(false);
    }
  };

  const handleRestoreClient = async (customer: Customer) => {
    setClientProcessing(true);
    try {
      const { error } = await supabase
        .from('workday_client_profiles')
        .update({
          soft_deleted_at: null,
          soft_deleted_by: null,
          status: 'active',
          updated_at: new Date().toISOString(),
        })
        .eq('id', customer.id);
      if (error) throw error;
      showClientMessage('success', 'Account restored. User can now log in.');
      setClientConfirmModal(null);
      await fetchCustomers();
    } catch {
      showClientMessage('error', 'Failed to restore account');
    } finally {
      setClientProcessing(false);
    }
  };

  const handleSaveEditClient = async () => {
    if (!editingClient) return;
    setClientProcessing(true);
    try {
      const { error } = await supabase
        .from('workday_client_profiles')
        .update({
          first_name: editingClient.first_name,
          last_name: editingClient.last_name,
          email: editingClient.email,
          phone: editingClient.phone,
          title: editingClient.title,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingClient.id);
      if (error) throw error;
      showClientMessage('success', 'Profile updated successfully');
      setShowEditClientModal(false);
      setEditingClient(null);
      await fetchCustomers();
    } catch {
      showClientMessage('error', 'Failed to update profile');
    } finally {
      setClientProcessing(false);
    }
  };

  const handleDeleteInterviewRequest = async (id: string) => {
    if (confirm('Are you sure you want to delete this interview request?')) {
      const { error } = await supabase
        .from('interview_requests')
        .delete()
        .eq('id', id);

      if (!error) {
        await fetchInterviewRequests();
      }
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="w-8 h-8 mr-3 text-[#40E0D0]" />
            Workday Client Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage invoices, credentials, and resources for Workday clients
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('clients')}
                className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                  activeTab === 'clients'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Users className="w-5 h-5 mr-2" />
                Clients
              </button>
              <button
                onClick={() => setActiveTab('interviews')}
                className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                  activeTab === 'interviews'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Briefcase className="w-5 h-5 mr-2" />
                Interview Requests
              </button>
              <button
                onClick={() => setActiveTab('invoices')}
                className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                  activeTab === 'invoices'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-5 h-5 mr-2" />
                Invoices
              </button>
              <button
                onClick={() => setActiveTab('credentials')}
                className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                  activeTab === 'credentials'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Key className="w-5 h-5 mr-2" />
                Credentials
              </button>
              <button
                onClick={() => setActiveTab('resources')}
                className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                  activeTab === 'resources'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Download className="w-5 h-5 mr-2" />
                Resources
              </button>
              <button
                onClick={() => setActiveTab('login-security')}
                className={`py-4 px-1 ml-4 inline-flex items-center border-b-2 font-medium text-sm ${
                  activeTab === 'login-security'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Shield className="w-5 h-5 mr-2" />
                Login Security
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab !== 'login-security' && (
            <div className="flex justify-between items-center mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                />
              </div>
              {activeTab !== 'clients' && activeTab !== 'interviews' && (
                <button
                  onClick={() => {
                    setEditingItem(null);
                    setShowModal(true);
                  }}
                  className="flex items-center px-4 py-2 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add {activeTab === 'invoices' ? 'Invoice' : activeTab === 'credentials' ? 'Credentials' : 'Resource'}
                </button>
              )}
            </div>
            )}

            {activeTab === 'login-security' && (
              <LoginSecurityTab userType="workday_client" />
            )}

            {activeTab !== 'login-security' && (loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
              </div>
            ) : (
              <>
                {activeTab === 'clients' && (
                  <>
                    {clientMessage && (
                      <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                        clientMessage.type === 'success'
                          ? 'bg-green-50 text-green-800 border border-green-200'
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        {clientMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        {clientMessage.text}
                      </div>
                    )}
                    <ClientList
                      customers={filteredCustomers}
                      onEdit={(c) => { setEditingClient({ ...c }); setShowEditClientModal(true); }}
                      onPause={handlePauseClient}
                      onSoftDelete={(c) => setClientConfirmModal({ type: 'soft_delete', customer: c })}
                      onHardDelete={(c) => setClientConfirmModal({ type: 'hard_delete', customer: c })}
                      onRestore={(c) => setClientConfirmModal({ type: 'restore', customer: c })}
                      onRefresh={fetchCustomers}
                    />
                  </>
                )}

                {activeTab === 'interviews' && (
                  <div className="space-y-4">
                    {filteredInterviewRequests.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Briefcase className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No interview requests found</p>
                      </div>
                    ) : (
                      filteredInterviewRequests.map((request) => (
                        <div
                          key={request.id}
                          className="p-6 border border-gray-200 rounded-lg hover:border-[#40E0D0] transition-colors bg-white"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-4">
                                <h3 className="font-semibold text-xl text-gray-900">Interview Request</h3>
                                <span className={`ml-3 px-3 py-1 rounded-full text-xs font-medium ${
                                  request.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : request.status === 'approved'
                                    ? 'bg-green-100 text-green-800'
                                    : request.status === 'completed'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                </span>
                              </div>

                              <div className="grid grid-cols-2 gap-6 mb-4">
                                <div className="border-l-4 border-[#40E0D0] pl-4">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Consultant Details</p>
                                  <p className="font-semibold text-lg mb-2 text-gray-900">{request.consultant_name}</p>
                                  <div className="space-y-1">
                                    <p className="text-sm text-gray-600 flex items-center">
                                      <FileText className="w-4 h-4 mr-2 text-[#40E0D0]" />
                                      <span className="font-medium">Modules:</span>&nbsp;{request.consultant_modules}
                                    </p>
                                    <p className="text-sm text-gray-600 flex items-center">
                                      <Clock className="w-4 h-4 mr-2 text-[#40E0D0]" />
                                      <span className="font-medium">Experience:</span>&nbsp;{request.consultant_experience}
                                    </p>
                                  </div>
                                </div>
                                <div className="border-l-4 border-blue-500 pl-4">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Client Details</p>
                                  <p className="font-semibold text-lg mb-1 text-gray-900">
                                    {request.workday_client_profiles
                                      ? `${request.workday_client_profiles.first_name} ${request.workday_client_profiles.last_name}`
                                      : request.client_contact_name}
                                  </p>
                                  {request.workday_client_profiles?.title && (
                                    <p className="text-sm text-gray-500 mb-2">{request.workday_client_profiles.title}</p>
                                  )}
                                  <div className="space-y-1">
                                    <p className="text-sm text-gray-600 flex items-center">
                                      <Building className="w-4 h-4 mr-2 text-blue-500" />
                                      <span className="font-medium">Company:</span>&nbsp;{request.workday_client_profiles?.companies?.name || request.client_company}
                                    </p>
                                    {request.workday_client_profiles?.companies?.website && (
                                      <p className="text-sm text-gray-600 flex items-start">
                                        <Building className="w-4 h-4 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />
                                        <span className="font-medium mr-1">Website:</span>
                                        <a href={request.workday_client_profiles.companies.website} target="_blank" rel="noopener noreferrer" className="text-[#40E0D0] hover:underline break-all">
                                          {request.workday_client_profiles.companies.website}
                                        </a>
                                      </p>
                                    )}
                                    {request.workday_client_profiles?.companies?.industry && (
                                      <p className="text-sm text-gray-600 flex items-center">
                                        <Briefcase className="w-4 h-4 mr-2 text-blue-500" />
                                        <span className="font-medium">Industry:</span>&nbsp;{request.workday_client_profiles.companies.industry}
                                      </p>
                                    )}
                                    {request.workday_client_profiles?.companies?.size && (
                                      <p className="text-sm text-gray-600 flex items-center">
                                        <Users className="w-4 h-4 mr-2 text-blue-500" />
                                        <span className="font-medium">Company Size:</span>&nbsp;{request.workday_client_profiles.companies.size}
                                      </p>
                                    )}
                                    {request.workday_client_profiles?.companies && (
                                      <>
                                        {(request.workday_client_profiles.companies.city || request.workday_client_profiles.companies.state || request.workday_client_profiles.companies.country) && (
                                          <p className="text-sm text-gray-600 flex items-start">
                                            <MapPin className="w-4 h-4 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />
                                            <span>
                                              {[
                                                request.workday_client_profiles.companies.city,
                                                request.workday_client_profiles.companies.state,
                                                request.workday_client_profiles.companies.country
                                              ].filter(Boolean).join(', ')}
                                            </span>
                                          </p>
                                        )}
                                      </>
                                    )}
                                    {!request.workday_client_profiles?.companies && request.workday_client_profiles?.country_region && (
                                      <p className="text-sm text-gray-600 flex items-center">
                                        <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                                        {request.workday_client_profiles.country_region}
                                      </p>
                                    )}
                                    <p className="text-sm text-gray-600 flex items-center">
                                      <Mail className="w-4 h-4 mr-2 text-blue-500" />
                                      {request.workday_client_profiles?.email || request.client_email}
                                    </p>
                                    <p className="text-sm text-gray-600 flex items-center">
                                      <Phone className="w-4 h-4 mr-2 text-blue-500" />
                                      {request.workday_client_profiles?.phone || request.client_phone || <span className="text-gray-400 italic">Not provided</span>}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {request.requested_interview_date && (
                                <div className="mb-3">
                                  <p className="text-xs text-gray-500 mb-1">Requested Interview Date</p>
                                  <p className="text-sm flex items-center">
                                    <Calendar className="w-4 h-4 mr-1 text-[#40E0D0]" />
                                    {formatDate(request.requested_interview_date)}
                                  </p>
                                </div>
                              )}

                              <div className="mb-3">
                                <p className="text-xs text-gray-500 mb-1">Client Notes</p>
                                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{request.notes}</p>
                              </div>

                              {request.admin_notes && (
                                <div className="mb-3">
                                  <p className="text-xs text-gray-500 mb-1">Admin Response</p>
                                  <div className="text-sm text-gray-700 bg-blue-50 border border-blue-200 p-3 rounded">
                                    <p>{request.admin_notes}</p>
                                    {request.admin_notes_created_at && (
                                      <p className="text-xs text-gray-500 mt-2">
                                        Added: {formatDate(request.admin_notes_created_at)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}

                              <p className="text-xs text-gray-500">
                                Submitted: {formatDate(request.created_at)}
                              </p>

                              <div className="mt-3 flex gap-2">
                                {request.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => openNotesModal(request, 'approved')}
                                      className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => openNotesModal(request, 'rejected')}
                                      className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {request.status === 'approved' && (
                                  <button
                                    onClick={() => handleUpdateInterviewStatus(request.id, 'completed')}
                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                  >
                                    Mark Completed
                                  </button>
                                )}
                                {(request.status === 'approved' || request.status === 'rejected') && (
                                  <button
                                    onClick={() => openNotesModal(request, request.status)}
                                    className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
                                  >
                                    Edit Notes
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleDeleteInterviewRequest(request.id)}
                                className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'invoices' && (
                  <div className="space-y-4">
                    {filteredInvoices.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No invoices found</p>
                      </div>
                    ) : (
                      filteredInvoices.map((invoice) => (
                        <div
                          key={invoice.id}
                          className="p-4 border border-gray-200 rounded-lg hover:border-[#40E0D0] transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <h3 className="font-semibold text-lg text-gray-900">Invoice #{invoice.invoice_number}</h3>
                                <span className={`ml-3 px-3 py-1 rounded-full text-xs font-medium ${
                                  invoice.is_paid
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {invoice.is_paid ? 'Paid' : 'Pending'}
                                </span>
                              </div>
                              <p className="text-gray-600 mb-2">{invoice.description}</p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <Users className="w-4 h-4 mr-1" />
                                  {getCustomerName(invoice.customer_profile_id)}
                                </span>
                                <span className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {formatDate(invoice.invoice_date)}
                                </span>
                                <span className="flex items-center">
                                  <DollarSign className="w-4 h-4 mr-1" />
                                  {formatCurrency(invoice.amount)}
                                </span>
                              </div>
                              {invoice.is_paid && invoice.paid_date && (
                                <p className="text-sm text-green-600 mt-1">
                                  Paid on {formatDate(invoice.paid_date)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setEditingItem(invoice);
                                  setShowModal(true);
                                }}
                                className="p-2 text-gray-600 hover:text-[#40E0D0] transition-colors"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteInvoice(invoice.id)}
                                className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'credentials' && (
                  <div className="space-y-4">
                    {filteredCredentials.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Key className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No credentials found</p>
                      </div>
                    ) : (
                      filteredCredentials.map((cred) => (
                        <div
                          key={cred.id}
                          className="p-4 border border-gray-200 rounded-lg hover:border-[#40E0D0] transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-2 text-gray-900">
                                {getCustomerName(cred.customer_profile_id)}
                              </h3>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-500">Username</p>
                                  <p className="font-mono">{cred.username}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Password</p>
                                  <p className="font-mono">••••••••</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-gray-500">Login URL</p>
                                  <a
                                    href={cred.login_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#40E0D0] hover:underline font-mono text-sm"
                                  >
                                    {cred.login_url}
                                  </a>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-gray-500">Access Granted</p>
                                  <p className="flex items-center">
                                    <Clock className="w-4 h-4 mr-1" />
                                    {formatDate(cred.access_granted_date)}
                                  </p>
                                </div>
                                {cred.notes && (
                                  <div className="col-span-2">
                                    <p className="text-gray-500">Notes</p>
                                    <p>{cred.notes}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setEditingItem(cred);
                                  setShowModal(true);
                                }}
                                className="p-2 text-gray-600 hover:text-[#40E0D0] transition-colors"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteCredential(cred.id)}
                                className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'resources' && (
                  <div className="space-y-4">
                    {filteredResources.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Download className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No resources found</p>
                      </div>
                    ) : (
                      filteredResources.map((resource) => (
                        <div
                          key={resource.id}
                          className="p-4 border border-gray-200 rounded-lg hover:border-[#40E0D0] transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <h3 className="font-semibold text-lg text-gray-900">{resource.title}</h3>
                                {resource.is_global && (
                                  <span className="ml-3 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Global
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600 mb-2">{resource.description}</p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                {!resource.is_global && resource.customer_profile_id && (
                                  <span className="flex items-center">
                                    <Users className="w-4 h-4 mr-1" />
                                    {getCustomerName(resource.customer_profile_id)}
                                  </span>
                                )}
                                <span>Type: {resource.resource_type}</span>
                              </div>
                              <a
                                href={resource.resource_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center mt-2 text-[#40E0D0] hover:underline text-sm"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                View Resource
                              </a>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setEditingItem(resource);
                                  setShowModal(true);
                                }}
                                className="p-2 text-gray-600 hover:text-[#40E0D0] transition-colors"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteResource(resource.id)}
                                className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      </div>

      {showNotesModal && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeNotesModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block w-full max-w-2xl overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedRequest.status === 'approved' ? 'Approve' : selectedRequest.status === 'rejected' ? 'Reject' : 'Edit Notes for'} Interview Request
                </h3>
                <button onClick={closeNotesModal} className="text-gray-600 hover:text-gray-800">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Request Details</p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Consultant:</span> {selectedRequest.consultant_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Client:</span> {selectedRequest.client_contact_name} ({selectedRequest.client_company})
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Response / Notes to Client {selectedRequest.status === 'approved' || selectedRequest.status === 'rejected' ? '(Optional)' : ''}
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={5}
                    placeholder={
                      selectedRequest.status === 'approved'
                        ? "e.g., Great match! We'll coordinate the interview details shortly..."
                        : selectedRequest.status === 'rejected'
                        ? "e.g., Thank you for your interest. At this time, we're looking for candidates with different experience..."
                        : "Add notes for the client..."
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedRequest.status === 'approved' || selectedRequest.status === 'rejected'
                      ? 'These notes will be sent to the client and visible in their dashboard.'
                      : 'Update the response that the client will see.'}
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Consultant Resume (Optional)
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#40E0D0] transition-colors cursor-pointer bg-gray-50">
                      <Upload className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        {resumeFile ? resumeFile.name : 'Choose file or drag here'}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                    {resumeFile && (
                      <button
                        type="button"
                        onClick={() => setResumeFile(null)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload the consultant's resume to attach to their profile. Accepted formats: PDF, DOC, DOCX
                  </p>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={closeNotesModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdateInterviewStatus(selectedRequest.id, selectedRequest.status, adminNotes.trim() || undefined)}
                    disabled={uploadingResume}
                    className={`flex items-center px-6 py-2 font-semibold rounded-lg transition-colors ${
                      uploadingResume
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : selectedRequest.status === 'approved'
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : selectedRequest.status === 'rejected'
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90'
                    }`}
                  >
                    {uploadingResume ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        {selectedRequest.status === 'approved' ? 'Approve Request' : selectedRequest.status === 'rejected' ? 'Reject Request' : 'Save Notes'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {clientConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            {clientConfirmModal.type === 'soft_delete' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Trash className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Soft Delete Account</h3>
                    <p className="text-sm text-gray-500">This action can be reversed within 30 days</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-2">
                  You are about to soft delete <span className="font-semibold text-gray-900">{clientConfirmModal.customer.first_name} {clientConfirmModal.customer.last_name}</span>.
                </p>
                <ul className="text-sm text-gray-500 space-y-1 mb-6 list-disc list-inside bg-orange-50 rounded-lg p-3">
                  <li>The user will immediately lose login access</li>
                  <li>Their data will be held in the database for 30 days</li>
                  <li>Use "Restore Account" to reverse this action</li>
                </ul>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setClientConfirmModal(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button onClick={() => handleSoftDeleteClient(clientConfirmModal.customer)} disabled={clientProcessing} className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50">
                    {clientProcessing ? 'Processing...' : 'Soft Delete'}
                  </button>
                </div>
              </>
            )}
            {clientConfirmModal.type === 'hard_delete' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Permanently Delete Account</h3>
                    <p className="text-sm text-red-500 font-medium">This cannot be undone</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Deleting <span className="font-semibold text-gray-900">{clientConfirmModal.customer.first_name} {clientConfirmModal.customer.last_name}</span> will remove their login access and profile.
                </p>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-3">
                  <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Permanently removed</p>
                  <ul className="text-sm text-red-700 space-y-0.5 list-disc list-inside">
                    <li>Login account &amp; profile</li>
                    <li>Team member invitations</li>
                    <li>Notifications</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 mb-6">
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Preserved for your records</p>
                  <ul className="text-sm text-green-700 space-y-0.5 list-disc list-inside">
                    <li>Invoices &amp; billing history</li>
                    <li>Credentials &amp; resources</li>
                    <li>Job postings &amp; service requirements</li>
                    <li>Interview requests</li>
                    <li>Login &amp; IP address history</li>
                  </ul>
                </div>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setClientConfirmModal(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button onClick={() => handleHardDeleteClient(clientConfirmModal.customer)} disabled={clientProcessing} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50">
                    {clientProcessing ? 'Deleting...' : 'Permanently Delete'}
                  </button>
                </div>
              </>
            )}
            {clientConfirmModal.type === 'restore' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <RotateCcw className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Restore Account</h3>
                    <p className="text-sm text-gray-500">Re-enable login access</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-6">
                  Restoring <span className="font-semibold text-gray-900">{clientConfirmModal.customer.first_name} {clientConfirmModal.customer.last_name}</span> will re-enable their login access and remove the deletion hold.
                </p>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setClientConfirmModal(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button onClick={() => handleRestoreClient(clientConfirmModal.customer)} disabled={clientProcessing} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50">
                    {clientProcessing ? 'Restoring...' : 'Restore Account'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showEditClientModal && editingClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Edit Client Profile</h3>
              <button onClick={() => { setShowEditClientModal(false); setEditingClient(null); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editingClient.first_name}
                    onChange={(e) => setEditingClient({ ...editingClient, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editingClient.last_name}
                    onChange={(e) => setEditingClient({ ...editingClient, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={editingClient.email}
                  onChange={(e) => setEditingClient({ ...editingClient, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input
                  type="text"
                  value={editingClient.phone || ''}
                  onChange={(e) => setEditingClient({ ...editingClient, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                <input
                  type="text"
                  value={editingClient.title || ''}
                  onChange={(e) => setEditingClient({ ...editingClient, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => { setShowEditClientModal(false); setEditingClient(null); }} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSaveEditClient} disabled={clientProcessing} className="px-4 py-2 rounded-lg bg-[#40E0D0] text-black text-sm font-semibold hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 flex items-center gap-2">
                <Save className="w-4 h-4" />
                {clientProcessing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <Modal
          activeTab={activeTab}
          editingItem={editingItem}
          customers={customers}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
          onSaveInvoice={handleSaveInvoice}
          onSaveCredential={handleSaveCredential}
          onSaveResource={handleSaveResource}
        />
      )}
    </AdminLayout>
  );
}

interface ClientDocument {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  description: string;
  uploaded_at: string;
}

// ─── Plan Management Panel ───────────────────────────────────────────────────

function PlanManagementPanel({ customer, onRefresh }: { customer: Customer & { plan?: string; total_seats?: number; downgrade_pending?: boolean; plan_expires_at?: string | null; pro_grace_until?: string | null }; onRefresh?: () => void }) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [seatsToAdd, setSeatsToAdd] = useState(1);

  const plan = (customer as any).plan || 'free';
  const totalSeats = (customer as any).total_seats || 0;
  const downgradePending = (customer as any).downgrade_pending || false;
  const planExpiresAt = (customer as any).plan_expires_at;
  const isPro = plan === 'pro';

  const setPlan = async (newPlan: 'free' | 'pro') => {
    setSaving(true);
    setMsg('');
    if (newPlan === 'pro') {
      const { error } = await supabase
        .from('workday_client_profiles')
        .update({ plan: 'pro', downgrade_pending: false, plan_expires_at: null, pending_checkout: false })
        .eq('id', customer.id);
      if (error) { setMsg('Error: ' + error.message); } else {
        // Also update all team members
        await supabase.from('workday_client_profiles').update({ plan: 'pro', pending_checkout: false }).eq('invited_by', customer.id);
        setMsg('Upgraded to Pro.');
        if (onRefresh) onRefresh();
      }
    } else {
      // Downgrade: call apply_pro_grace_period function
      // First set plan_expires_at to end of current month if not set
      const endOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59).toISOString();
      await supabase.from('workday_client_profiles').update({ plan_expires_at: endOfMonth }).eq('id', customer.id);
      const { error } = await supabase.rpc('apply_pro_grace_period', { owner_id: customer.id });
      if (error) { setMsg('Error: ' + error.message); } else {
        await supabase.from('workday_client_profiles').update({ plan: 'free', pending_checkout: false }).eq('id', customer.id);
        setMsg('Downgraded to Free. Grace period set through end of billing month.');
        if (onRefresh) onRefresh();
      }
    }
    setSaving(false);
    setTimeout(() => setMsg(''), 5000);
  };

  const purchaseSeats = async () => {
    if (seatsToAdd < 1) return;
    setSaving(true);
    const { error } = await supabase.from('seat_purchases').insert({
      account_owner_id: customer.id,
      seats_purchased: seatsToAdd,
      amount_paid: seatsToAdd * 99,
      per_seat_price: 99,
      notes: `Admin-added ${seatsToAdd} seat(s) for ${customer.first_name} ${customer.last_name}`,
    });
    if (!error) {
      await supabase.from('workday_client_profiles').update({ total_seats: (totalSeats || 0) + seatsToAdd }).eq('id', customer.id);
      setMsg(`Added ${seatsToAdd} seat(s) at $99 each.`);
      if (onRefresh) onRefresh();
    } else {
      setMsg('Error: ' + error.message);
    }
    setSaving(false);
    setTimeout(() => setMsg(''), 5000);
  };

  const forceExpireGrace = async () => {
    setSaving(true);
    const { error } = await supabase.rpc('expire_pro_grace_seats');
    if (!error) { setMsg('Grace period expired — seats reverted to free.'); if (onRefresh) onRefresh(); }
    else setMsg('Error: ' + error.message);
    setSaving(false);
    setTimeout(() => setMsg(''), 5000);
  };

  return (
    <div className="space-y-3 text-xs">
      {/* Plan badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2.5 py-1 rounded-full font-bold text-xs ${isPro ? 'bg-[#40E0D0] text-black' : 'bg-gray-200 text-gray-700'}`}>
          {isPro ? 'PRO' : 'FREE'}
        </span>
        {downgradePending && (
          <span className="px-2.5 py-1 rounded-full font-semibold text-xs bg-amber-100 text-amber-800">
            Downgrade Pending — Grace until {planExpiresAt ? new Date(planExpiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
          </span>
        )}
        <span className="text-gray-500">{totalSeats > 0 ? `${totalSeats} purchased seats` : 'No seat cap (free)'}</span>
      </div>

      {/* Plan toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {!isPro ? (
          <button onClick={() => setPlan('pro')} disabled={saving}
            className="px-3 py-1.5 rounded-lg bg-[#40E0D0] text-black text-xs font-semibold hover:bg-[#40E0D0]/80 transition-colors disabled:opacity-50">
            Upgrade to Pro
          </button>
        ) : (
          <button onClick={() => setPlan('free')} disabled={saving}
            className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 text-xs font-semibold hover:bg-amber-200 transition-colors disabled:opacity-50">
            Downgrade to Free (set grace period)
          </button>
        )}
        {downgradePending && (
          <button onClick={forceExpireGrace} disabled={saving}
            className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-xs font-semibold hover:bg-red-200 transition-colors disabled:opacity-50">
            Force-Expire Grace Now
          </button>
        )}
      </div>

      {/* Additional seat purchase */}
      {isPro && (
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Add seats ($99 each):</span>
          <input type="number" min={1} max={20} value={seatsToAdd} onChange={e => setSeatsToAdd(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-[#40E0D0]" />
          <button onClick={purchaseSeats} disabled={saving}
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
            {saving ? '...' : `Add ${seatsToAdd} seat${seatsToAdd > 1 ? 's' : ''} — $${seatsToAdd * 99}`}
          </button>
        </div>
      )}

      {msg && <p className={`text-xs font-medium ${msg.startsWith('Error') ? 'text-red-600' : 'text-green-700'}`}>{msg}</p>}
    </div>
  );
}

function ClientExpandedRow({ customer, onRefresh }: { customer: Customer; onRefresh?: () => void }) {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoadingDocs(true);
      const { data, error } = await supabase
        .from('workday_client_documents')
        .select('*')
        .eq('client_id', customer.id)
        .order('uploaded_at', { ascending: false });

      if (!error && data) {
        setDocuments(data);
      }
      setLoadingDocs(false);
    };

    fetchDocuments();
  }, [customer.id]);

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getSignedUrl = async (fileUrl: string, fileName: string) => {
    const pathMatch = fileUrl.match(/workday-client-documents\/(.+)$/);
    if (!pathMatch) {
      window.open(fileUrl, '_blank');
      return;
    }
    const filePath = pathMatch[1];
    const { data, error } = await supabase.storage
      .from('workday-client-documents')
      .createSignedUrl(filePath, 60);

    if (!error && data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  return (
    <>
    <tr>
      <td colSpan={8} className="px-6 py-5 bg-gray-50 border-t border-gray-100">
        <div className="grid grid-cols-3 gap-6 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Company Details</p>
            <div className="space-y-1 text-gray-700">
              {customer.companies?.website && (
                <p className="flex items-start gap-1.5">
                  <Building className="w-4 h-4 text-[#40E0D0] mt-0.5 shrink-0" />
                  <a href={customer.companies.website} target="_blank" rel="noopener noreferrer" className="text-[#40E0D0] hover:underline break-all">
                    {customer.companies.website}
                  </a>
                </p>
              )}
              {(customer.companies?.size || customer.company_size) && (
                <p className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-gray-400 shrink-0" />
                  {customer.companies?.size || customer.company_size}
                </p>
              )}
              {(customer.companies?.city || customer.companies?.state || customer.companies?.country) && (
                <p className="flex items-start gap-1.5">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  {[customer.companies?.address, customer.companies?.city, customer.companies?.state, customer.companies?.country].filter(Boolean).join(', ')}
                </p>
              )}
              {!customer.companies && customer.country_region && (
                <p className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                  {customer.country_region}
                </p>
              )}
              {!customer.companies?.website && !customer.companies?.size && !customer.companies?.city && <span className="text-gray-400 italic text-xs">No additional details</span>}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Project Details</p>
            <div className="space-y-1 text-gray-700">
              {customer.hiring_needs && (
                <div>
                  <p className="text-xs text-gray-400">Services Needed</p>
                  <p>{customer.hiring_needs}</p>
                </div>
              )}
              {customer.budget_range && (
                <div>
                  <p className="text-xs text-gray-400">Budget Range</p>
                  <p>{customer.budget_range}</p>
                </div>
              )}
              {customer.timeline && (
                <div>
                  <p className="text-xs text-gray-400">Timeline</p>
                  <p>{customer.timeline}</p>
                </div>
              )}
              {customer.project_requirements?.trim() && (
                <div>
                  <p className="text-xs text-gray-400 mt-2">Project Requirements</p>
                  <p className="text-gray-700 bg-white border border-gray-200 rounded p-2 text-sm leading-relaxed mt-1">{customer.project_requirements}</p>
                </div>
              )}
              {!customer.hiring_needs && !customer.budget_range && !customer.timeline && !customer.project_requirements?.trim() && <span className="text-gray-400 italic text-xs">No project details</span>}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Plan & Seat Management</p>
            <PlanManagementPanel customer={customer} onRefresh={onRefresh} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Uploaded Documents</p>
            {loadingDocs ? (
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <div className="animate-spin rounded-full h-3 w-3 border-t border-b border-[#40E0D0]"></div>
                Loading...
              </div>
            ) : documents.length === 0 ? (
              <span className="text-gray-400 italic text-xs">No documents uploaded</span>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.file_name}</p>
                      <p className="text-xs text-gray-400">
                        {doc.file_type?.toUpperCase()} {doc.file_size ? `· ${formatFileSize(doc.file_size)}` : ''} · {new Date(doc.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <button
                      onClick={() => getSignedUrl(doc.file_url, doc.file_name)}
                      className="ml-3 flex items-center gap-1 px-2 py-1 text-xs text-[#40E0D0] hover:bg-[#40E0D0]/10 rounded transition-colors shrink-0"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
    {customer.deletion_request?.status === 'pending' && (
      <tr>
        <td colSpan={8} className="px-6 pb-4 bg-gray-50">
          <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-2">Account Deletion Request</p>
          <div className="flex flex-wrap items-start gap-4 p-3 bg-red-50 rounded-lg border border-red-200 text-sm">
            <div>
              <span className="font-medium text-red-900">Requested:</span>{' '}
              <span className="text-red-700">{new Date(customer.deletion_request.requested_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            {customer.deletion_request.reason && (
              <div>
                <span className="font-medium text-red-900">Reason:</span>{' '}
                <span className="text-red-700">{customer.deletion_request.reason}</span>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  await supabase.from('account_deletion_requests').update({ status: 'dismissed', resolved_at: new Date().toISOString() }).eq('id', customer.deletion_request!.id);
                  if (onRefresh) onRefresh();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </td>
      </tr>
    )}
    </>
  );
}

interface ClientListProps {
  customers: Customer[];
  onEdit: (customer: Customer) => void;
  onPause: (customer: Customer) => void;
  onSoftDelete: (customer: Customer) => void;
  onHardDelete: (customer: Customer) => void;
  onRestore: (customer: Customer) => void;
  onRefresh?: () => void;
}

function ClientList({ customers, onEdit, onPause, onSoftDelete, onHardDelete, onRestore, onRefresh }: ClientListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
        setDropdownPos(null);
      }
    };
    if (openDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  if (customers.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>No clients found</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10"></th>
            <th className="px-4 py-3 w-10"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {customers.map((customer) => {
            const isSoftDeleted = !!customer.soft_deleted_at;
            const isPaused = customer.status === 'paused';
            return (
              <React.Fragment key={customer.id}>
                <tr
                  className={`cursor-pointer transition-colors ${isSoftDeleted ? 'bg-red-50/40 opacity-60 hover:bg-red-50/60' : 'hover:bg-gray-50'}`}
                  onClick={() => setExpandedId(expandedId === customer.id ? null : customer.id)}
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{customer.first_name} {customer.last_name}</p>
                      {customer.title && <p className="text-xs text-gray-500">{customer.title}</p>}
                      {isSoftDeleted && <span className="text-xs text-red-600 font-medium bg-red-100 px-1.5 py-0.5 rounded">Soft Deleted</span>}
                      {!isSoftDeleted && isPaused && <span className="text-xs text-orange-500 font-medium">Paused</span>}
                      {customer.deletion_request?.status === 'pending' && (
                        <span className="text-xs text-red-700 font-semibold bg-red-100 border border-red-200 px-1.5 py-0.5 rounded flex items-center gap-1 w-fit mt-0.5">
                          <Trash2 className="w-3 h-3" />
                          Delete Requested
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700">{customer.companies?.name || <span className="text-gray-400 italic text-xs">—</span>}</p>
                    {customer.companies?.industry && <p className="text-xs text-gray-400">{customer.companies.industry}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <a href={`mailto:${customer.email}`} className="text-sm text-[#40E0D0] hover:underline" onClick={(e) => e.stopPropagation()}>
                      {customer.email}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{customer.phone || <span className="text-gray-400 text-xs">—</span>}</td>
                  <td className="px-4 py-3">
                    {customer.role ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {customer.role === 'account_owner' ? 'Account Owner' : customer.role}
                      </span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {customer.status ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                      </span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {expandedId === customer.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="relative" ref={openDropdown === customer.id ? dropdownRef : null}>
                      <button
                        onClick={(e) => {
                          const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                          if (openDropdown === customer.id) {
                            setOpenDropdown(null);
                            setDropdownPos(null);
                          } else {
                            setOpenDropdown(customer.id);
                            setDropdownPos({ top: rect.bottom + window.scrollY + 4, right: window.innerWidth - rect.right });
                          }
                        }}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                        title="Actions"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openDropdown === customer.id && dropdownPos && (
                        <div
                          style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right, zIndex: 9999 }}
                          className="w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 divide-y divide-gray-50"
                        >
                          <div className="py-1">
                            <button
                              onClick={() => { onEdit(customer); setOpenDropdown(null); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Edit className="h-4 w-4 text-gray-400" />
                              Edit Profile
                            </button>
                            <button
                              onClick={() => { onPause(customer); setOpenDropdown(null); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {isPaused
                                ? <Play className="h-4 w-4 text-green-500" />
                                : <Pause className="h-4 w-4 text-yellow-500" />}
                              {isPaused ? 'Activate Account' : 'Pause Account'}
                            </button>
                          </div>
                          <div className="py-1">
                            {isSoftDeleted ? (
                              <button
                                onClick={() => { onRestore(customer); setOpenDropdown(null); }}
                                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
                              >
                                <RotateCcw className="h-4 w-4" />
                                Restore Account
                              </button>
                            ) : (
                              <button
                                onClick={() => { onSoftDelete(customer); setOpenDropdown(null); }}
                                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 transition-colors"
                              >
                                <Trash className="h-4 w-4" />
                                Soft Delete (30-day hold)
                              </button>
                            )}
                            <button
                              onClick={() => { onHardDelete(customer); setOpenDropdown(null); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                              Hard Delete (Permanent)
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>

                {expandedId === customer.id && (
                  <ClientExpandedRow customer={customer} onRefresh={onRefresh} />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ModalProps {
  activeTab: 'invoices' | 'credentials' | 'resources';
  editingItem: any;
  customers: Customer[];
  onClose: () => void;
  onSaveInvoice: (invoice: Partial<Invoice>) => void;
  onSaveCredential: (credential: Partial<Credential>) => void;
  onSaveResource: (resource: Partial<Resource>) => void;
}

function Modal({ activeTab, editingItem, customers, onClose, onSaveInvoice, onSaveCredential, onSaveResource }: ModalProps) {
  const [formData, setFormData] = useState<any>(editingItem || {});

  useEffect(() => {
    if (!editingItem) {
      if (activeTab === 'invoices') {
        setFormData({
          customer_profile_id: '',
          invoice_number: '',
          invoice_date: new Date().toISOString().split('T')[0],
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          amount: 0,
          description: '',
          invoice_url: '',
          is_paid: false,
          paid_date: null
        });
      } else if (activeTab === 'credentials') {
        setFormData({
          customer_profile_id: '',
          username: '',
          password: '',
          login_url: '',
          access_granted_date: new Date().toISOString().split('T')[0],
          notes: ''
        });
      } else if (activeTab === 'resources') {
        setFormData({
          customer_profile_id: null,
          title: '',
          description: '',
          resource_url: '',
          resource_type: 'document',
          is_global: false
        });
      }
    }
  }, [editingItem, activeTab]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeTab === 'invoices') {
      onSaveInvoice(formData);
    } else if (activeTab === 'credentials') {
      onSaveCredential(formData);
    } else if (activeTab === 'resources') {
      onSaveResource(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block w-full max-w-2xl overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">
              {editingItem ? 'Edit' : 'Add'} {activeTab === 'invoices' ? 'Invoice' : activeTab === 'credentials' ? 'Credentials' : 'Resource'}
            </h3>
            <button onClick={onClose} className="text-gray-600 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {activeTab === 'invoices' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                  <select
                    required
                    value={formData.customer_profile_id || ''}
                    onChange={(e) => setFormData({ ...formData, customer_profile_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  >
                    <option value="">Select a customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.first_name} {customer.last_name} ({customer.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                    <input
                      type="text"
                      required
                      value={formData.invoice_number || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.amount || 0}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date</label>
                    <input
                      type="date"
                      required
                      value={formData.invoice_date || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                    <input
                      type="date"
                      required
                      value={formData.due_date || ''}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    required
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice URL (optional)</label>
                  <input
                    type="url"
                    value={formData.invoice_url || ''}
                    onChange={(e) => setFormData({ ...formData, invoice_url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_paid || false}
                    onChange={(e) => setFormData({
                      ...formData,
                      is_paid: e.target.checked,
                      paid_date: e.target.checked ? new Date().toISOString().split('T')[0] : null
                    })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Mark as paid</label>
                </div>

                {formData.is_paid && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Paid Date</label>
                    <input
                      type="date"
                      value={formData.paid_date || ''}
                      onChange={(e) => setFormData({ ...formData, paid_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'credentials' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                  <select
                    required
                    value={formData.customer_profile_id || ''}
                    onChange={(e) => setFormData({ ...formData, customer_profile_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  >
                    <option value="">Select a customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.first_name} {customer.last_name} ({customer.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                    <input
                      type="text"
                      required
                      value={formData.username || ''}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                    <input
                      type="text"
                      required
                      value={formData.password || ''}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Login URL</label>
                  <input
                    type="url"
                    required
                    value={formData.login_url || ''}
                    onChange={(e) => setFormData({ ...formData, login_url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Access Granted Date</label>
                  <input
                    type="date"
                    required
                    value={formData.access_granted_date || ''}
                    onChange={(e) => setFormData({ ...formData, access_granted_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {activeTab === 'resources' && (
              <div className="space-y-4">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    checked={formData.is_global || false}
                    onChange={(e) => setFormData({
                      ...formData,
                      is_global: e.target.checked,
                      customer_profile_id: e.target.checked ? null : formData.customer_profile_id
                    })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Global Resource (visible to all clients)</label>
                </div>

                {!formData.is_global && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                    <select
                      required
                      value={formData.customer_profile_id || ''}
                      onChange={(e) => setFormData({ ...formData, customer_profile_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    >
                      <option value="">Select a customer</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.first_name} {customer.last_name} ({customer.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    required
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    required
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resource URL</label>
                  <input
                    type="url"
                    required
                    value={formData.resource_url || ''}
                    onChange={(e) => setFormData({ ...formData, resource_url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resource Type</label>
                  <select
                    required
                    value={formData.resource_type || 'document'}
                    onChange={(e) => setFormData({ ...formData, resource_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  >
                    <option value="document">Document</option>
                    <option value="video">Video</option>
                    <option value="link">Link</option>
                    <option value="guide">Guide</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center px-6 py-2 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
              >
                <Save className="w-5 h-5 mr-2" />
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
